import dayjs from "dayjs";
import logo from "../../../../assets/images/logo_light.svg";
import reportHeader from "../../../../assets/images/report_header.svg";
import { formatAmount } from "../../../../utils/appUtils";
import Footerlogo from "../../../../assets/images/Logo.png";

const getInvoiceRoot = (reportData = {}) => reportData?.invoiceResponse || reportData?.InvoiceResponse || reportData || {};
const getDisplayName = (response = {}) => `${response?.firstName || ""} ${response?.middleName || ""} ${response?.lastName || ""}`?.trim();

const buildRowsFromCurrentShape = (invoiceRoot = {}, sacNumber) => {
    const consultationPaymentDetails = invoiceRoot?.consultationPaymentDetails || {};
    const consultationFeesDetails = consultationPaymentDetails?.consultationFeesDetails || {};
    const baseRows = [];

    if (Number(consultationFeesDetails?.consultationTotalFee || 0) > 0) {
        baseRows.push({
            description: "Consultation Fee",
            sac: sacNumber,
            amount: Number(consultationFeesDetails?.consultationTotalFee || 0),
            amountWithoutGst: Number(consultationFeesDetails?.consultationFeeWithoutGst || 0),
            gstAmount: Number(consultationFeesDetails?.consultationFeeGst || 0)
        });
    }
    (consultationPaymentDetails?.addOnList || [])?.forEach((addOnDetail) => {
        baseRows.push({
            description: `Add-On: ${addOnDetail?.addOn || "Nil"}`,
            sac: sacNumber,
            amount: Number(addOnDetail?.totalAmount || 0),
            amountWithoutGst: Number(addOnDetail?.amountWithOutGst || 0),
            gstAmount: Number(addOnDetail?.gstAmount || 0)
        });
    });
    const rawExtendList = consultationPaymentDetails?.extendConsultionDetailsList || invoiceRoot?.extendConsultionDetailsList || [];
    const extendDetailsList = Array.isArray(rawExtendList) ? rawExtendList : rawExtendList ? [rawExtendList] : [];
    extendDetailsList?.forEach((extendDetail) => {
        const extendAddOns = Array.isArray(extendDetail?.addOnList) ? extendDetail.addOnList : [];
        const extendAddOnWithoutGst = extendAddOns.reduce((sum, item) => sum + (Number(item?.amountWithOutGst) || 0), 0);
        const extendAddOnGst = extendAddOns.reduce((sum, item) => sum + (Number(item?.gstAmount) || 0), 0);
        const extendAddOnTotal = extendAddOns.reduce((sum, item) => sum + (Number(item?.totalAmount) || 0), 0);

        const extendAmountWithoutGst = Math.max(0, Number(extendDetail?.amountWithoutGst || 0) - extendAddOnWithoutGst);
        const extendGstAmount = Math.max(0, Number(extendDetail?.gstAmount || 0) - extendAddOnGst);
        const extendTotalAmount = Math.max(0, Number(extendDetail?.totalAmount || 0) - extendAddOnTotal);

        if (extendTotalAmount > 0 || extendAmountWithoutGst > 0 || extendAddOns.length === 0) {
            baseRows.push({
                description: "Extend Time",
                sac: sacNumber,
                amount: extendTotalAmount,
                amountWithoutGst: extendAmountWithoutGst,
                gstAmount: extendGstAmount
            });
        }

        extendAddOns.forEach((addOnDetail) => {
            baseRows.push({
                description: `Add-On: ${addOnDetail?.addOn || "Nil"}`,
                sac: sacNumber,
                amount: Number(addOnDetail?.totalAmount || 0),
                amountWithoutGst: Number(addOnDetail?.amountWithOutGst || 0),
                gstAmount: Number(addOnDetail?.gstAmount || 0)
            });
        });
    });
    const recordDetail = consultationPaymentDetails?.recordDetail || {};
    if (Number(recordDetail?.totalRecordAmount || 0) > 0) {
        baseRows.push({
            description: "Recording",
            sac: sacNumber,
            amount: Number(recordDetail?.totalRecordAmount || 0),
            amountWithoutGst: Number(recordDetail?.recordAmountWithoutGst || 0),
            gstAmount: Number(recordDetail?.recordGstAmount || 0)
        });
    }
    return baseRows;
};

const buildRowsFromRefund = (invoiceRoot = {}) => {
    const refundAmount = Number(invoiceRoot?.totalAmount || 0);
    return [{
        description: "Refund to Customer",
        sac: "999700",
        amount: refundAmount,
        amountWithoutGst: refundAmount,
        gstAmount: 0
    }];
};

const buildModel = (reportData = {}, title = "REFUND RECEIPT", sacNumber) => {
    const invoiceRoot = getInvoiceRoot(reportData);
    const isLegacyShape = Array.isArray(reportData?.rows) || Boolean(reportData?.invoice);
    const GSTIN = "33AAUCM1496R1ZG";
    const OFFICE_ADDRESS = "FlyDesk, Kasi Arcade, No.116/1, Suit No.1, 6th Floor, Sri Theagaraya Road, T.Nagar, Chennai, Tamil Nadu - 600 017";
    let invoiceRows, totalValue, supplierName, supplierAddress, supplierPhone, supplierEmail, invoiceNo, consultationNo, invoiceDate, advanceReceivedAmount, isRefundReceipt;

    if (isLegacyShape) {
        invoiceRows = reportData?.rows || [];
        totalValue = Number(reportData?.totals?.totalValue || 0);
        supplierName = reportData?.supplier?.name || "Nil";
        supplierAddress = reportData?.supplier?.address || "Nil";
        supplierPhone = reportData?.supplier?.phone || "Nil";
        supplierEmail = reportData?.supplier?.email || "Nil";
        invoiceNo = reportData?.invoice?.serialNo || "Nil";
        consultationNo = reportData?.invoice?.consultationId || reportData?.invoice?.consultationNo || "Nil";
        invoiceDate = reportData?.invoice?.invoiceDate ? dayjs(reportData?.invoice?.invoiceDate).format("DD MMM,YYYY") : "Nil";
        advanceReceivedAmount = Number(reportData?.totals?.advanceAmount || 0);
        isRefundReceipt = false;
    } else {
        isRefundReceipt = invoiceRoot?.paymentCategory === "REFUND_TO_CUSTOMER";
        invoiceRows = isRefundReceipt ? buildRowsFromRefund(invoiceRoot) : buildRowsFromCurrentShape(invoiceRoot, sacNumber);
        totalValue = invoiceRows?.reduce((accumulator, row) => accumulator + (Number(row?.amount) || 0), 0);
        supplierName = getDisplayName(invoiceRoot?.customerResponse) || "Nil";
        supplierAddress = "Nil";
        supplierPhone = invoiceRoot?.customerResponse?.mobileNumber || "Nil";
        supplierEmail = invoiceRoot?.customerResponse?.email || "Nil";
        invoiceNo = invoiceRoot?.taxInvoiceNo || invoiceRoot?.advanceInvoiceNo || invoiceRoot?.consultationNo || "Nil";
        consultationNo = invoiceRoot?.consultationNo || invoiceRoot?.consultationId || "Nil";
        invoiceDate = invoiceRoot?.transactionDate ? dayjs(invoiceRoot?.transactionDate).format("DD MMM,YYYY") : "Nil";
        advanceReceivedAmount = 0;
    }

    return {
        supplierName,
        supplierAddress,
        supplierPhone,
        supplierEmail,
        invoiceNo,
        consultationNo,
        invoiceDate,
        invoiceTitle: isLegacyShape ? (reportData?.invoice?.invoiceTitle || title) : title,
        invoiceRows,
        totalValue,
        gstin: reportData?.footer?.gstin || GSTIN,
        officeAddress: reportData?.footer?.officeAddress || OFFICE_ADDRESS,
        advanceInvoiceNo: isLegacyShape ? (reportData?.invoice?.advanceInvoiceNo || "") : (invoiceRoot?.advanceInvoiceNo || ""),
        advanceReceivedAmount
    };
};

const RefundReceiptReport = ({ reportData = {}, sacNumber }) => {
    const reportModel = buildModel(reportData, "REFUND RECEIPT", sacNumber);

    return <div className="tax-invoice-detail-report refund-receipt-report">
        <div className="tax-detail-sheet">
            <div className="tax-detail-top-header">
                <div className="tax-detail-branding">
                    <img src={logo} alt="MLawyer" />
                    <h1>M-Lawyer</h1>
                </div>
                <div className="tax-detail-header-shape" aria-hidden="true">
                    <img src={reportHeader} alt="" />
                </div>
            </div>

            <div className="tax-detail-head-info">
                <div className="tax-detail-supplier-block">
                    <h3>Invoice To:</h3>
                    <p>{reportModel?.supplierName || "Nil"}</p>
                    <p>{reportModel?.supplierAddress || "Nil"}</p>
                    <p>{reportModel?.supplierPhone || "Nil"}</p>
                    <p>{reportModel?.supplierEmail || "Nil"}</p>
                </div>
                <div className="tax-detail-invoice-meta">
                    <p className="tax-detail-si-no">Receipt No : {reportModel?.invoiceNo || "Nil"}</p>
                    <p className="tax-detail-date">Receipt Date : {reportModel?.invoiceDate || "Nil"}</p>
                    <p className="tax-detail-date">Consultation ID: {reportModel?.consultationNo || "Nil"}</p>
                </div>
            </div>

            <h2 className="tax-detail-title">{reportModel?.invoiceTitle || "REFUND RECEIPT"}</h2>

            <table className="tax-detail-table">
                <thead>
                    <tr>
                        <th>SNo</th>
                        <th>SAC</th>
                        <th>Description</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        reportModel?.invoiceRows?.map((row, index) => <tr key={row?.id || `${index}`}>
                            <td>{index + 1}</td>
                            <td>{row?.sac || "Nil"}</td>
                            <td>{row?.description || "Nil"}</td>
                            <td>₹{formatAmount(row?.amountWithoutGst ?? row?.amount)}</td>
                        </tr>)
                    }
                </tbody>
            </table>

            <div className="tax-detail-total-section">
                {
                    reportModel?.advanceInvoiceNo
                        ? <div className="tax-detail-total-row">
                            <span>Advance Received({reportModel?.advanceInvoiceNo})</span>
                            <span>₹{formatAmount(reportModel?.advanceReceivedAmount)}</span>
                        </div>
                        : null
                }
                <div className="tax-detail-total-row tax-detail-grand-total">
                    <span>Total</span>
                    <span>₹{formatAmount(reportModel?.totalValue)}</span>
                </div>
            </div>

            <div className="tax-detail-signature">
                <p>Authorized Signature</p>
            </div>
        </div>

        <div className="tax-detail-footer">
            <div className="tax-detail-footer-brand">
                <img src={Footerlogo} alt="MLawyer" />
                <h5>MyLawyer</h5>
                <h4>GSTIN: {reportModel?.gstin}</h4>
            </div>
            <div className="tax-detail-footer-address">
                <h4>Office Address:</h4>
                <p>{reportModel?.officeAddress}</p>
            </div>
        </div>
    </div>;
};

export default RefundReceiptReport;
